/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 01/02/22
 */

import React from 'react';
import { Link, Menu, MenuButton, MenuDivider, MenuGroup, MenuItem, MenuList } from '@chakra-ui/react';
import { useAuth } from '../auth/useAuth';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { ProfileMenuButton } from '@/components/ProfileMenu/ProfileMenuButton';
import { useNavigate } from 'react-router-dom';
import config from '@/config';

interface ProfileMenuProps {
    name: string;
}

export const ProfileMenu = (props: ProfileMenuProps) => {
    const { logout, authState } = useAuth();
    const { currentAssignment, activeAssignments, inactiveAssignments, setAssignment } = useAssignment();
    const handleLogOut = () => {
        logout();
    };
    const navigate = useNavigate();

    return (
        <Menu matchWidth>
            <MenuButton
                as={ProfileMenuButton}
                aria-label="Account menu"
                userName={props.name}
                currentAssignment={currentAssignment}
            />
            <MenuList>
                {process.env.NODE_ENV === 'development' && (
                    // TODO remove this for production
                    // Helper for copying access token to Swagger interface
                    <MenuItem
                        onClick={() => {
                            if (authState.accessToken) {
                                navigator.clipboard.writeText(authState.accessToken);
                            }
                        }}
                    >
                        Copy Access Token
                    </MenuItem>
                )}
                <MenuDivider />
                <MenuGroup title="Switch Assignment:">
                    {(activeAssignments ?? []).map((assignment) => (
                        <MenuItem
                            key={assignment.id}
                            isDisabled={currentAssignment && assignment.id === currentAssignment.id}
                            onClick={() => {
                                setAssignment(assignment);
                                navigate('/');
                            }}
                        >
                            {assignment.assignmentNumber} - {assignment.name}
                        </MenuItem>
                    ))}
                </MenuGroup>
                {inactiveAssignments.length > 0 && (
                    <>
                        <MenuDivider />
                        <MenuGroup title="Inactive Assignments:">
                            {(inactiveAssignments ?? []).map((assignment) => (
                                <MenuItem
                                    key={assignment.id}
                                    isDisabled={currentAssignment && assignment.id === currentAssignment.id}
                                    onClick={() => {
                                        setAssignment(assignment);
                                        navigate('/');
                                    }}
                                >
                                    {assignment.assignmentNumber} - {assignment.name}
                                </MenuItem>
                            ))}
                        </MenuGroup>
                    </>
                )}
                <MenuDivider />
                <MenuGroup>
                    {config.urls.aiAssistant && (
                        <Link href={config.urls.aiAssistant} isExternal color={'black'}>
                            <MenuItem>AI Assistant</MenuItem>
                        </Link>
                    )}
                    <Link
                        href="https://studentutsedu.sharepoint.com/sites/UTSWorkPlanningTool"
                        isExternal
                        color={'black'}
                        _hover={{
                            underline: 'none',
                        }}
                    >
                        <MenuItem>Supporting Resources</MenuItem>
                    </Link>
                    <MenuItem onClick={handleLogOut}>Logout</MenuItem>
                </MenuGroup>
            </MenuList>
        </Menu>
    );
};
